import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Spinner } from './Spinner';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'plain' | 'danger';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, marks the button busy and prevents further clicks. */
  loading?: boolean;
  fullWidth?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Buttons trigger actions (docs/ux-richtlinien.md §1, §7).
 *
 * - `primary`: filled, the one main action of a view
 * - `secondary`: grey fill, further actions
 * - `soft`: tinted fill, a secondary action that should stand out a little
 * - `ghost`: no fill, neutral (icon buttons, toolbars)
 * - `plain`: no fill, tinted text (inline actions like "Alle anzeigen")
 * - `danger`: filled red, only for confirming irreversible actions
 *
 * Buttons without children become square icon buttons and need an `aria-label`.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  iconStart,
  iconEnd,
  disabled,
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps) {
  const iconOnly = children === undefined || children === null;
  const classes = [
    styles.button,
    styles[variant],
    styles[size],
    iconOnly && styles.iconOnly,
    fullWidth && styles.fullWidth,
    loading && styles.loading,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : iconStart}
      {!iconOnly && (
        <span className={styles.label} data-button-label="">
          {children}
        </span>
      )}
      {!loading && iconEnd}
    </button>
  );
}
